// ===============================================================================
// Sidebar.js - Main Sidebar Navigation for SplitMon (Mobile-Optimized)
// ===============================================================================
// Location: /var/www/wow/frontend/src/Sidebar.js
// Contains sidebar menu (desktop) and bottom navigation (mobile)
// Updated: Added bottom tab bar for mobile/PWA experience
// ===============================================================================

import React, { useState, useEffect } from "react";
import { authAPI } from "./api";
import Dashboard from "./Dashboard";
import GroupDetail from "./GroupDetail";
import CreateGroup from "./CreateGroup";
import JoinGroup from "./JoinGroup";
import Settings from "./Settings";
import "./Sidebar.css";
import SearchExpenses from "./SearchExpenses";
import UpgradeMembership from "./UpgradeMembership";
import ExpenseAnalysis from "./ExpenseAnalysis";
import BusinessGroups from "./BusinessGroups";
import BusinessExpenseAnalysis from "./BusinessExpenseAnalysis";

function Sidebar({ user, onLogout }) {
  // ===============================================================================
  // State Management
  // ===============================================================================
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [businessRefreshKey, setBusinessRefreshKey] = useState(0);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // ===============================================================================
  // Detect Mobile/Desktop
  // ===============================================================================
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 768) {
        setShowMoreMenu(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===============================================================================
  // Close "More" menu when clicking outside
  // ===============================================================================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMoreMenu && !e.target.closest('.more-menu-container')) {
        setShowMoreMenu(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMoreMenu]);

  // ===============================================================================
  // Logout Handler
  // ===============================================================================
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error("Logout error:", err);
    }
    onLogout();
  };

  // ===============================================================================
  // Navigation Handlers
  // ===============================================================================
  const navigateToDashboard = () => {
    setActiveMenu("dashboard");
    setSelectedGroupId(null);
    setShowMoreMenu(false);
  };

  const navigateToGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setActiveMenu("group-detail");
    setShowMoreMenu(false);
  };

  const navigateToCreateGroup = () => {
    setActiveMenu("create-group");
    setShowMoreMenu(false);
  };

  const navigateToJoinGroup = () => {
    setActiveMenu("join-group");
    setShowMoreMenu(false);
  };

  const navigateToSettings = () => {
    setActiveMenu("settings");
    setShowMoreMenu(false);
  };

  const navigateToSearchExpenses = () => {
    if (user?.is_premium) {
      setActiveMenu("search-expenses");
    } else {
      setActiveMenu("upgrade-membership");
    }
    setShowMoreMenu(false);
  };

  const navigateToExpenseAnalysis = () => {
    if (user?.is_premium) {
      setActiveMenu("expense-analysis");
    } else {
      setActiveMenu("upgrade-membership");
    }
    setShowMoreMenu(false);
  };

  const refreshDashboard = () => {
    setRefreshKey(prev => prev + 1);
    navigateToDashboard();
  };

  const navigateToBusinessGroups = () => {
    if (user?.is_premium) {
      setBusinessRefreshKey(prev => prev + 1);
      setActiveMenu("business-groups");
    } else {
      setActiveMenu("upgrade-membership");
    }
    setShowMoreMenu(false);
  };

  const navigateToBusinessExpenseAnalysis = () => {
    if (user?.is_premium) {
      setActiveMenu("business-expense-analysis");
    } else {
      setActiveMenu("upgrade-membership");
    }
    setShowMoreMenu(false);
  };

  // ===============================================================================
  // Render Content Based on Active Menu
  // ===============================================================================
  const renderContent = () => {
    switch (activeMenu) {
      case "search-expenses":
        return (
          <SearchExpenses 
            user={user}
            onBack={navigateToDashboard}
          />
        );
      case "expense-analysis":
        return (
          <ExpenseAnalysis 
            user={user}
            onBack={navigateToDashboard}
          />
        );
      case "business-groups":
        return (
          <BusinessGroups
            key={businessRefreshKey}
            user={user}
            onBack={navigateToDashboard}
          />
        );
      case "upgrade-membership":
        return (
          <UpgradeMembership 
            user={user}
            onBack={navigateToDashboard}
          />
        );
      case "dashboard":
        return (
          <Dashboard 
            key={refreshKey}
            user={user}
            onSelectGroup={navigateToGroup}
            onCreateGroup={navigateToCreateGroup}
            onJoinGroup={navigateToJoinGroup}
            onUpgrade={() => setActiveMenu("upgrade-membership")}
          />
        );
      case "group-detail":
        return (
          <GroupDetail 
            groupId={selectedGroupId}
            user={user}
            onBack={navigateToDashboard}
            onGroupDeleted={refreshDashboard}
          />
        );
      case "create-group":
        return (
          <CreateGroup 
            onBack={navigateToDashboard}
            onGroupCreated={(group) => {
              navigateToGroup(group.id);
            }}
          />
        );
      case "join-group":
        return (
          <JoinGroup 
            onBack={navigateToDashboard}
            onGroupJoined={(group) => {
              navigateToGroup(group.id);
            }}
          />
        );
      case "business-expense-analysis":
        return (
          <BusinessExpenseAnalysis 
            user={user}
            onBack={navigateToDashboard}
          />
        );
      case "settings":
        return (
          <Settings 
            user={user}
            onBack={navigateToDashboard}
            onLogout={handleLogout}
          />
        );
      default:
        return <Dashboard user={user} onSelectGroup={navigateToGroup} />;
    }
  };

  // ===============================================================================
  // Check if current menu is in "More" section (for mobile bottom nav)
  // ===============================================================================
  const isMoreMenuActive = () => {
    return ['search-expenses', 'create-group', 'join-group',
            'upgrade-membership', 'settings'].includes(activeMenu);
  };

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="sidebar-layout">
      {/* =========================================================================
          Desktop Sidebar (hidden on mobile)
          ========================================================================= */}
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <span className="logo-icon">💰</span>
          {!sidebarCollapsed && (
            <div className="logo-text">
              <span className="logo-title">SplitMon</span>
              <span className="logo-subtitle">Split Monitoring App</span>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeMenu === "dashboard" ? "active" : ""}`}
            onClick={navigateToDashboard}
          >
            <span className="nav-icon">🏠</span>
            {!sidebarCollapsed && <span className="nav-text">Basic Groups</span>}
          </button>

          <button
            className={`nav-item ${activeMenu === "create-group" ? "active" : ""}`}
            onClick={navigateToCreateGroup}
          >
            <span className="nav-icon">➕</span>
            {!sidebarCollapsed && <span className="nav-text">Create Basic Group</span>}
          </button>

          <button
            className={`nav-item ${activeMenu === "join-group" ? "active" : ""}`}
            onClick={navigateToJoinGroup}
          >
            <span className="nav-icon">🙋‍♂️</span>
            {!sidebarCollapsed && <span className="nav-text">Join Shared Group</span>}
          </button>

          <button
            className={`nav-item ${activeMenu === "settings" ? "active" : ""}`}
            onClick={navigateToSettings}
          >
            <span className="nav-icon">🧑‍💻</span>
            {!sidebarCollapsed && <span className="nav-text">User Settings</span>}
          </button>

          <div className="nav-divider" />

          <button
            className={`nav-item ${activeMenu === "search-expenses" || activeMenu === "upgrade-membership" ? "active" : ""}`}
            onClick={navigateToSearchExpenses}
          >
            <span className="nav-icon">🔍</span>
            {!sidebarCollapsed && (
              <span className="nav-text">
                Search Expenses
                {!user?.is_premium && <span className="premium-indicator">⭐</span>}
              </span>
            )}
          </button>

          <button
            className={`nav-item ${activeMenu === "expense-analysis" ? "active" : ""}`}
            onClick={navigateToExpenseAnalysis}
          >
            <span className="nav-icon">📊</span>
            {!sidebarCollapsed && (
              <span className="nav-text">
                Expense Analysis
                {!user?.is_premium && <span className="premium-indicator">⭐</span>}
              </span>
            )}
          </button>

          <button
            className={`nav-item ${activeMenu === "business-groups" ? "active" : ""}`}
            onClick={navigateToBusinessGroups}
          >
            <span className="nav-icon">💼</span>
            {!sidebarCollapsed && (
              <span className="nav-text">
                Business Groups
                {!user?.is_premium && <span className="premium-indicator">⭐</span>}
              </span>
            )}
          </button>

          <button
            className={`nav-item ${activeMenu === "business-expense-analysis" ? "active" : ""}`}
            onClick={navigateToBusinessExpenseAnalysis}
          >
            <span className="nav-icon">📈</span>
            {!sidebarCollapsed && (
              <span className="nav-text">
                Business Analysis
                {!user?.is_premium && <span className="premium-indicator">⭐</span>}
              </span>
            )}
          </button>
        </nav>

        {/* User Info & Logout */}
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            {!sidebarCollapsed && <span className="nav-text">Logout</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button 
          className="collapse-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? "→" : "←"}
        </button>
      </div>

      {/* =========================================================================
          Main Content
          ========================================================================= */}
      <main className="main-content">
        {renderContent()}
      </main>

      {/* =========================================================================
          Mobile Bottom Navigation (visible only on mobile)
          ========================================================================= */}
      {isMobile && (
        <nav className="bottom-nav">
          {/* Dashboard/Home */}
          <button
            className={`bottom-nav-item ${activeMenu === "dashboard" || activeMenu === "group-detail" ? "active" : ""}`}
            onClick={navigateToDashboard}
          >
            <span className="bottom-nav-icon">🏠</span>
            <span className="bottom-nav-label">Basic Groups</span>
          </button>

          {/* Business Groups (Premium) */}
          <button
            className={`bottom-nav-item ${activeMenu === "business-groups" ? "active" : ""}`}
            onClick={navigateToBusinessGroups}
          >
            <span className="bottom-nav-icon">💼</span>
            <span className="bottom-nav-label">Biz Groups</span>
            {!user?.is_premium && <span className="bottom-nav-badge">⭐</span>}
          </button>

          {/* Business Analysis (Premium) */}
          <button
            className={`bottom-nav-item ${activeMenu === "business-expense-analysis" ? "active" : ""}`}
            onClick={navigateToBusinessExpenseAnalysis}
          >
            <span className="bottom-nav-icon">📈</span>
            <span className="bottom-nav-label">Biz Stats</span>
            {!user?.is_premium && <span className="bottom-nav-badge">⭐</span>}
          </button>

          {/* Personal Expense Analysis (Premium) */}
          <button
            className={`bottom-nav-item ${activeMenu === "expense-analysis" ? "active" : ""}`}
            onClick={navigateToExpenseAnalysis}
          >
            <span className="bottom-nav-icon">📊</span>
            <span className="bottom-nav-label">Exp Analysis</span>
            {!user?.is_premium && <span className="bottom-nav-badge">⭐</span>}
          </button>

          {/* More Menu */}
          <div className="more-menu-container">
            <button
              className={`bottom-nav-item ${isMoreMenuActive() ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreMenu(!showMoreMenu);
              }}
            >
              <span className="bottom-nav-icon">☰</span>
              <span className="bottom-nav-label">More</span>
            </button>

            {/* More Menu Popup */}
            {showMoreMenu && (
              <div className="more-menu-popup">
                <div className="more-menu-header">
                  <span>More Options</span>
                  <button className="more-menu-close" onClick={() => setShowMoreMenu(false)}>×</button>
                </div>

                <button className="more-menu-item" onClick={navigateToCreateGroup}>
                  <span className="more-menu-icon">➕</span>
                  <span>Create Basic Group</span>
                </button>

                <button className="more-menu-item" onClick={navigateToJoinGroup}>
                  <span className="more-menu-icon">🙋</span>
                  <span>Join Shared Group</span>
                </button>

                <div className="more-menu-divider" />
                
                <button className="more-menu-item" onClick={navigateToSearchExpenses}>
                  <span className="more-menu-icon">🔍</span>
                  <span>Search Expenses</span>
                  {!user?.is_premium && <span className="premium-badge">PRO</span>}
                </button>

                <div className="more-menu-divider" />

                <button className="more-menu-item" onClick={navigateToSettings}>
                  <span className="more-menu-icon">⚙️</span>
                  <span>User Settings</span>
                </button>

                <button className="more-menu-item logout" onClick={handleLogout}>
                  <span className="more-menu-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </nav>
      )}
    </div>
  );
}

export default Sidebar;

